import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Grid2,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  Product,
  StockCheckSuggestedProducts,
} from "../../../../../models/Stock/Stock";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useStores } from "../../../use-stores";
import AppSearchDropDown from "../../../../../views/components/common/filter/AppSearchDropDown";
import { SortType } from "../../../../../models/SortType";
import { observer } from "mobx-react";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import ImageGalleryPopup from "../../../../../views/components/common/pop-up/ImageGalleryPopup";


interface SearchTextState {
  productSearch: string;
  vendorSearch: string;
}

interface StockCheckDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  requestId: number;
  product: Product;
  onSubmit: (
    requestId: number,
    updatedProduct: Product[],
    suggestedProduct: StockCheckSuggestedProducts[]
  ) => void;
  isViewOnly?: boolean;
}

const InStock: FilterValue[] = [
  { id: "-1", value: "Select" },
  { id: "0", value: "No" },
  { id: "1", value: "Yes" },
];

const StockCheckDetailsPopup: React.FC<StockCheckDetailsPopupProps> = ({
  open,
  onClose,
  requestId,
  product,
  onSubmit,
  isViewOnly = false,
}) => {
  const theme = useTheme();

  const { productStore, vendorStore, stockStore, userStore } = useStores();
  const [productData, setProductData] = useState<Product>(() => ({
    ...product,
  }));

  const [updatedSuggestedProducts, setUpdatedSuggestedProducts] = useState<
    StockCheckSuggestedProducts[] | []
  >([]);

  const [searchTextStates, setSearchTextStates] = useState<SearchTextState[]>(
    []
  );

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("Suggested Product Image");
  const [previewImages, setPreviewImages] = useState<
    { id?: number; src: string; alt?: string }[]
  >([]);

  useEffect(() => {
    setProductData({ ...product });
  }, [product]);

  useEffect(() => {
    if (open) {
      stockStore.setVendorSearchInput("");
      stockStore.setUseManualVendorEntry(false);
      // Always start with empty suggested products on popup open.
      setUpdatedSuggestedProducts([]);
      setSearchTextStates([]);
    }
  }, [open, product, stockStore]);

  const handleInputChange = (field: keyof Product, value: string | number) => {
    const shouldClearSuggestedProducts =
      field === "in_stock" && Number(value) !== 0;

    setProductData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (shouldClearSuggestedProducts) {
      setUpdatedSuggestedProducts([]);
      setSearchTextStates([]);
    }
  };

  const handleSuggestedProductChange = (
    index: number,
    field: keyof StockCheckSuggestedProducts,
    value: string | number
  ): void => {
    setUpdatedSuggestedProducts((prev) =>
      (prev || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const getProductImageUrl = (productItem?: {
    image_url?: string;
    image_urls?: { url: string }[];
  }) =>
    productItem?.image_urls?.[0]?.url || productItem?.image_url || "";

  const handleProductSelect = (index: number, val: FilterValue[]) => {
    if (val.length > 0) {
      const selectedProduct = val[0];
      const fullProduct = productStore.products.find(
        (item) => String(item.id) === String(selectedProduct.id),
      );
      const imageUrl = getProductImageUrl(fullProduct);

      setUpdatedSuggestedProducts((prev) =>
        (prev || []).map((item, i) =>
          i === index
            ? {
              ...item,
              id: Number(selectedProduct.id),
              product_name: selectedProduct.value,
              cost_price: fullProduct?.cost_price ?? 0,
              product_image_urls: imageUrl ? [imageUrl] : [],
            }
            : item
        )
      );
    } else {
      setUpdatedSuggestedProducts((prev) =>
        (prev || []).map((item, i) =>
          i === index
            ? {
              ...item,
              id: 0,
              product_name: "",
              cost_price: 0,
              product_image_urls: [],
            }
            : item
        )
      );
    }
  };

  const handleVendorSelect = (index: number, val: FilterValue[]) => {
    if (val.length > 0) {
      const selectedProduct = val[0];
      setUpdatedSuggestedProducts((prev) =>
        (prev || []).map((item, i) =>
          i === index
            ? {
              ...item,
              vendor_id: Number(selectedProduct.id),
              vendor_name: selectedProduct.value,
            }
            : item
        )
      );
    } else {
      setUpdatedSuggestedProducts((prev) =>
        (prev || []).map((item, i) =>
          i === index
            ? {
              ...item,
              vendor_id: 0,
              vendor_name: "",
            }
            : item
        )
      );
    }
  };

  useEffect(() => {
    if (updatedSuggestedProducts) {
      setSearchTextStates(
        updatedSuggestedProducts.map(() => ({
          productSearch: "",
          vendorSearch: "",
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSuggestedProducts?.length]);

  const handleAddSuggestedProduct = (): void => {
    setUpdatedSuggestedProducts((prev) => [
      ...(prev || []),
      {
        id: 0,
        product_id: product.product_id,
        expected_raw_inward_timeline: 0,
        expected_print_inward_timeline: 0,
        vendor_id: 0,
        vendor_stock: 0,
        product_name: "",
        vendor_name: "",
        cost_price: 0,
        product_image_urls: [] as string[],
      } as StockCheckSuggestedProducts,
    ]);

    setSearchTextStates((prev) => [
      ...prev,
      { productSearch: "", vendorSearch: "" },
    ]);
  };

  const handleDeleteSuggestedProduct = (index: number): void => {
    setUpdatedSuggestedProducts((prev) =>
      (prev || []).filter((_, i) => i !== index)
    );

    setSearchTextStates((prev) => (prev || []).filter((_, i) => i !== index));
  };

  const handleOpenImagePreview = (suggestedProduct: StockCheckSuggestedProducts) => {
    const images = (suggestedProduct.product_image_urls || [])
      .filter(Boolean)
      .map((url, idx) => ({
        id: idx,
        src: url,
        alt: suggestedProduct.product_name || "Suggested Product",
      }));

    if (!images.length) return;

    setPreviewImages(images);
    setPreviewTitle(suggestedProduct.product_name || "Suggested Product Image");
    setIsImagePreviewOpen(true);
  };

  const updateSearchText = (
    index: number,
    type: "product" | "vendor",
    value: string
  ) => {
    setSearchTextStates((prev) =>
      prev.map((state, i) =>
        i === index
          ? {
            ...state,
            [type === "product" ? "productSearch" : "vendorSearch"]: value,
          }
          : state
      )
    );
  };

  let apiTimer: any = 0;
  const handleSearchProduct = (searchText: string, index: number) => {
    if (apiTimer) clearTimeout(apiTimer);

    updateSearchText(index, "product", searchText || "");

    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        const params = {
          name: searchText,
          sortBy: "created_date",
          sortType: SortType.DESC,
          page_no: 1,
        };
        productStore.getProducts(params);
      }, 1000);
  };

  const handleSearchVendor = (searchText: string, index: number) => {
    if (apiTimer) clearTimeout(apiTimer);

    updateSearchText(index, "vendor", searchText || "");

    if (searchText && searchText.length > 2) {
      apiTimer = setTimeout(() => {
        const params = {
          search_text: searchText,
          page_no: 1,
        };
        vendorStore.getVendors(params);
      }, 1000);
    }
  };

  const validateForm = (): boolean => {
    if (parseInt(productData.in_stock!?.toString()) === 1) {
      if (
        !productData.vendor_name?.trim() ||
        !productData.expected_raw_inward_timeline ||
        !productData.expected_print_inward_timeline
      ) {
        setErrorMessage("Please fill in all required fields");
        return false;
      }
    } else if (parseInt(productData.in_stock!?.toString()) === 0) {
      if (updatedSuggestedProducts.length > 0) {
        const invalidProducts = updatedSuggestedProducts
          .map((product, index) => {
            const missingFields = [];
            if (!product.product_name) missingFields.push("Product");
            if (!product.vendor_name) missingFields.push("Vendor");
            if (!product.vendor_stock) missingFields.push("Stock Quantity");
            if (!product.expected_raw_inward_timeline) {
              missingFields.push("Expected Raw Inward Timeline");
            }
            if (!product.expected_print_inward_timeline) {
              missingFields.push("Expected Print Inward Timeline");
            }

            return missingFields.length
              ? `Product Option ${index + 1}: ${missingFields.join(", ")}`
              : null;
          })
          .filter(Boolean);

        if (invalidProducts.length) {
          setErrorMessage(`Please fill in all required fields`);
          return false;
        }
      }
    }

    setErrorMessage("");
    return true;
  };

  useEffect(() => {
    setErrorMessage("");
  }, [productData, updatedSuggestedProducts]);

  const handleConfirm = (): void => {
    if (validateForm()) {
      if (parseInt(productData.in_stock!?.toString()) === 0) {
        onSubmit(requestId, [productData], updatedSuggestedProducts || []);
      } else if (parseInt(productData.in_stock!?.toString()) === 1) {
        onSubmit(requestId, [productData], []);
      }
      onClose();
    }
  };

  const showCostPriceField =
    ((productData.vendor_id &&
      productData?.vendor_id !== Number(process.env.REACT_APP_VENDOR_ID)) ||
      (productData.vendor_name &&
        productData.vendor_name.length > 0 &&
        productData.vendor_name.toLowerCase() !== "swageazy")) &&
    userStore.hasPermission(EmployeeRoles.CHANGE_COST_PRICE);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            margin: { xs: "16px", sm: "24px" },
            position: "relative",
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              pointerEvents: "none",
              zIndex: 1,
              background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
            },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          mt: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
          borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
        }}
      >
        <CustomTypography variant="heading3">
          Stock Check Request Details
        </CustomTypography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme.palette.customColors.greyTextColor,
            "&:hover": {
              backgroundColor: theme.palette.customColors.lightGreyColor,
              color: theme.palette.customColors.blackBorder,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          maxHeight: "65vh",
          overflowY: "auto",
        }}
      >

        <Box sx={{ display: "flex", gap: 6, mt: 2 }}>
          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Holding Period
            </CustomTypography>
            <FormTextField
              sx={{ mb: 3 }}
              label="Holding Period"
              value={productData.holding_period || ""}
              variant="outlined"
              onChange={(id, value) => {
                handleInputChange("holding_period", parseInt(value!) || 0);
              }}
              disabled={isViewOnly}
            />
          </Box>

          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              In stock
            </CustomTypography>
            <AppDropDown
              borderColor={theme.palette.customColors.blackBorder}
              value={
                parseInt(productData.in_stock!?.toString()) === 1
                  ? InStock[2]?.id
                  : parseInt(productData.in_stock!?.toString()) === 0
                    ? InStock[1]?.id
                    : InStock[0]?.id
              }
              list={InStock}
              isMultiSelect={false}
              title=""
              onSingleSelectionChange={(val) => {
                handleInputChange("in_stock", val);
              }}
              disabled={isViewOnly}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 6, mt: 1 }}>
          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Vendor Name
            </CustomTypography>

            <AppSearchDropDown
              borderColor={theme.palette.customColors.blackBorder}
              isMultiSelect={false}
              required={true}
              title="Vendor"
              maxWidth="248px"
              fontSize="0.9rem"
              textFieldValue={stockStore.vendorSearchInput}
              list={[
                ...vendorStore.vendors.map((item) => ({
                  id: String(item.id),
                  value: item.name!,
                })),
                { id: "other", value: "Other" },
              ]}
              value={
                stockStore.useManualVendorEntry
                  ? [
                    {
                      id: "other",
                      value: "Other",
                    },
                  ]
                  : productData.vendor_name
                    ? [
                      {
                        id: String(
                          productData.vendor_id
                            ? productData.vendor_id
                            : "selected_" + productData.vendor_name
                        ),
                        value: productData.vendor_name,
                      },
                    ]
                    : []
              }
              local={false}
              handleSearch={(text) => {
                // Always update the input text regardless of length
                stockStore.setVendorSearchInput(text || "");
                if (text && text.toLowerCase() !== "other" && text.length > 2) {
                  const params = {
                    search_text: text,
                    page_no: 1,
                  };
                  vendorStore.getVendors(params);
                }
              }}
              handleSelect={(val: FilterValue[]) => {
                if (val.length > 0) {
                  const selectedVendor = val[0];
                  if (selectedVendor.value === "Other") {
                    stockStore.setUseManualVendorEntry(true);
                    handleInputChange("vendor_name", "");
                    stockStore.setVendorSearchInput("");
                  } else {
                    stockStore.setUseManualVendorEntry(false);
                    handleInputChange("vendor_name", selectedVendor.value);
                    stockStore.setVendorSearchInput("");
                  }
                } else {
                  stockStore.setUseManualVendorEntry(false);
                  handleInputChange("vendor_name", "");
                  stockStore.setVendorSearchInput("");
                }
              }}
              disabled={isViewOnly}
            />

            {stockStore.useManualVendorEntry && (
              <FormTextField
                required={true}
                label="Custom Vendor Name"
                value={productData.vendor_name || ""}
                variant="outlined"
                sx={{ mb: 3, maxWidth: "248px", mt: 2 }}
                onChange={(id, value) => {
                  handleInputChange("vendor_name", value!);
                }}
                disabled={isViewOnly}
              />
            )}
          </Box>

          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Stock Quantity
            </CustomTypography>

            <FormTextField
              label="Stock Quantity"
              value={productData.vendor_stock || ""}
              variant="outlined"
              type="number"
              sx={{ mb: 2 }}
              onChange={(id, value) => {
                handleInputChange("vendor_stock", value!);
              }}
              disabled={isViewOnly}
            />
          </Box>

        </Box>

        <Box sx={{ display: "flex", gap: 6, mt: 1 }}>
          {((productData.vendor_id &&
            productData?.vendor_id !==
            Number(process.env.REACT_APP_VENDOR_ID)) ||
            (productData.vendor_name &&
              productData.vendor_name.length > 0 &&
              productData.vendor_name.toLowerCase() !== "swageazy")) &&
            userStore.hasPermission(EmployeeRoles.CHANGE_COST_PRICE) && (
              <Box sx={{ width: "50%" }}>
                <CustomTypography
                  variant="heading6"
                  style={{
                    marginBottom: "1rem",
                    color: theme.palette.customColors.greyTextColor,
                  }}
                >
                  Cost Price
                </CustomTypography>
                <FormTextField
                  label="Cost Price"
                  required={true}
                  value={productData.cost_price || ""}
                  variant="outlined"
                  type="number"
                  onChange={(id, value) => {
                    const numValue = parseFloat(value!) || 0;
                    if (numValue < 0) {
                      return;
                    }
                    handleInputChange("cost_price", numValue);
                  }}
                  disabled={isViewOnly}
                />
              </Box>
            )}
        </Box>
        <Box sx={{ display: "flex", gap: 6, mt: 1 }}>
          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Raw Inward Timeline
            </CustomTypography>
            <FormTextField
              label="Raw Inward (days)"
              required={true}
              value={productData.expected_raw_inward_timeline || ""}
              variant="outlined"
              type="number"
              sx={{ mb: 2 }}
              onChange={(id, value) => {
                handleInputChange("expected_raw_inward_timeline", value!);
              }}
              disabled={isViewOnly}
            />
          </Box>
          <Box sx={{ width: "50%" }}>
            <CustomTypography
              variant="heading6"
              style={{
                marginBottom: "1rem",
                color: theme.palette.customColors.greyTextColor,
              }}
            >
              Print Inward Timeline
            </CustomTypography>
            <FormTextField
              label="Print Inward (days)"
              required={true}
              value={productData.expected_print_inward_timeline || ""}
              variant="outlined"
              type="number"
              onChange={(id, value) => {
                handleInputChange("expected_print_inward_timeline", value!);
              }}
              disabled={isViewOnly}
            />
          </Box>
        </Box>
        <Box sx={{ width: "100%", mt: 2 }}>
          <CustomTypography
            variant="heading6"
            style={{
              marginBottom: "1rem",
              color: theme.palette.customColors.greyTextColor,
            }}
          >
            Notes (max 200 characters)
          </CustomTypography>
          <FormTextField
            label="Notes"
            value={productData.sourcing_notes || ""}
            variant="outlined"
            multiline
            maxLength={200}
            onChange={(id, value) => {
              handleInputChange("sourcing_notes", value!);
            }}
            sx={{ mb: 3, width: "100%" }}
            disabled={isViewOnly}
          />
        </Box>

        <hr
          style={{
            marginTop: "1rem",
            border: `0.5px solid ${theme.palette.customColors.grayBackGroundColor}`,
            borderStyle: "solid",
          }}
        />

        {parseInt(productData.in_stock!?.toString()) === 0 && (
          <Box sx={{ mb: 2, mt: 2, p: 1.5, borderRadius: "10px", border: `1px solid ${theme.palette.customColors.greyBorder}`, backgroundColor: theme.palette.common.white }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Box>
                <CustomTypography variant="heading6" fontWeight={600}>
                  Suggest Products
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color={theme.palette.customColors.greyTextColor}
                >
                  Add alternate products when out of stock
                </CustomTypography>
              </Box>
              {!isViewOnly && (
                <AppButton
                  startIcon={<AddIcon />}
                  variant="primary"
                  onClick={() => handleAddSuggestedProduct()}
                  label="Add"
                  customStyles={{
                    backgroundColor: "transparent",
                    padding: 0.5,
                    margin: 0,
                    fontSize: "0.85rem",
                    color: theme.palette.customColors.pinkDark,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: theme.palette.customColors.pinkDark,
                      boxShadow: "none",
                    },
                  }}
                />
              )}
            </Box>


            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {(updatedSuggestedProducts || []).map(
                (suggestedProduct, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      p: 1.5,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.customColors.greyBorder}`,
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                    }}
                  >
                    {!isViewOnly && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSuggestedProduct(index)}
                        sx={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          color: theme.palette.error.main,
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}

                    <CustomTypography
                      variant="caption"
                      fontWeight={700}
                      style={{
                        display: "inline-block",
                        marginBottom: "1rem",
                        padding: "0.25rem",
                        borderRadius: "0.125rem",
                        backgroundColor:
                          theme.palette.customColors.grayBackGroundColor,
                      }}
                    >
                      Option {index + 1}
                    </CustomTypography>

                    {suggestedProduct.product_name && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                          pr: 3,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            backgroundColor:
                              theme.palette.customColors.greyBackground,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {suggestedProduct.product_image_urls?.[0] ? (
                            <Box sx={{ position: "relative" }}>
                              <Box
                                component="img"
                                src={suggestedProduct.product_image_urls[0]}
                                alt={suggestedProduct.product_name}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  objectFit: "contain",
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenImagePreview(suggestedProduct)
                                }
                                sx={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  width: 24,
                                  height: 24,
                                  backgroundColor:
                                    theme.palette.customColors.backdrop,
                                  color: theme.palette.customColors.white,
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.customColors.backdrop,
                                  },
                                }}
                              >
                                <VisibilityOutlinedIcon
                                  sx={{ width: 14, height: 14 }}
                                />
                              </IconButton>
                            </Box>
                          ) : (
                            <CustomTypography variant="caption">
                              N/A
                            </CustomTypography>
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <CustomTypography
                            variant="body2"
                            fontWeight={600}
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {suggestedProduct.product_name}
                          </CustomTypography>
                          <CustomTypography
                            variant="caption"
                            color={
                              theme.palette.customColors.greyTextColor
                            }
                          >
                            Cost Price: ₹{suggestedProduct.cost_price ?? 0}
                            {suggestedProduct.id
                              ? ` · ID ${suggestedProduct.id}`
                              : ""}
                          </CustomTypography>
                        </Box>
                      </Box>
                    )}

                    <Grid2 container spacing={1.5}>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ width: "100%" }}>
                          <AppSearchDropDown
                            borderColor={
                              theme.palette.customColors.blackBorder
                            }
                            isMultiSelect={false}
                            required={true}
                            title="Product"
                            value={
                              suggestedProduct.product_name
                                ? [
                                  {
                                    id: String(suggestedProduct.id),
                                    value: suggestedProduct.product_name,
                                  },
                                ]
                                : []
                            }
                            fontSize="0.9rem"
                            list={
                              productStore.products.map((item) => ({
                                id: String(item.id),
                                value: item.name!,
                              })) || []
                            }
                            local={false}
                            handleSearch={(text) =>
                              handleSearchProduct(text!, index)
                            }
                            handleSelect={(val: FilterValue[]) => {
                              handleProductSelect(index, val);
                              updateSearchText(index, "product", "");
                            }}
                            key={`product-${index}`}
                            textFieldValue={
                              searchTextStates[index]?.productSearch || ""
                            }
                            disabled={isViewOnly}
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ width: "100%" }}>
                          <AppSearchDropDown
                            borderColor={
                              theme.palette.customColors.blackBorder
                            }
                            isMultiSelect={false}
                            required={true}
                            title="Vendor"
                            value={
                              suggestedProduct.vendor_name
                                ? [
                                  {
                                    id: String(suggestedProduct.vendor_id),
                                    value: suggestedProduct.vendor_name,
                                  },
                                ]
                                : []
                            }
                            fontSize="0.9rem"
                            list={
                              vendorStore.vendors.map((item) => ({
                                id: String(item.id),
                                value: item.name!,
                              })) || []
                            }
                            local={false}
                            handleSelect={(val: FilterValue[]) => {
                              handleVendorSelect(index, val);
                              updateSearchText(index, "vendor", "");
                            }}
                            handleSearch={(text) =>
                              handleSearchVendor(text!, index)
                            }
                            key={`vendor-${index}`}
                            textFieldValue={
                              searchTextStates[index]?.vendorSearch || ""
                            }
                            disabled={isViewOnly}
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <FormTextField
                          label="Quantity"
                          required={true}
                          value={suggestedProduct.vendor_stock || ""}
                          onChange={(e, val) =>
                            handleSuggestedProductChange(
                              index,
                              "vendor_stock",
                              parseInt(val!),
                            )
                          }
                          variant="outlined"
                          type="number"
                          disabled={isViewOnly}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <FormTextField
                          label="Raw Inward (days)"
                          required={true}
                          value={
                            suggestedProduct.expected_raw_inward_timeline ||
                            ""
                          }
                          onChange={(e, val) =>
                            handleSuggestedProductChange(
                              index,
                              "expected_raw_inward_timeline",
                              parseInt(val!),
                            )
                          }
                          variant="outlined"
                          type="number"
                          disabled={isViewOnly}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <FormTextField
                          label="Print Inward (days)"
                          required={true}
                          value={
                            suggestedProduct.expected_print_inward_timeline ||
                            ""
                          }
                          onChange={(e, val) =>
                            handleSuggestedProductChange(
                              index,
                              "expected_print_inward_timeline",
                              parseInt(val!),
                            )
                          }
                          variant="outlined"
                          type="number"
                          disabled={isViewOnly}
                        />
                      </Grid2>
                    </Grid2>
                  </Box>
                ),
              )}
            </Box>

          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
          borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
          backgroundColor: theme.palette.customColors.lightGreyColor,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            color: theme.palette.error.main,
            fontSize: "0.75rem",
            minHeight: "18px",
          }}
        >
          {errorMessage || ""}
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <AppButton variant="grayscale" onClick={onClose} label="Cancel" />
          {!isViewOnly && (
            <AppButton
              variant="primary"
              onClick={handleConfirm}
              label="Confirm"
              disabled={
                Number(productData.in_stock) < 0 ||
                productData.in_stock === null
              }
            />
          )}
        </Box>
      </DialogActions>
      <ImageGalleryPopup
        open={isImagePreviewOpen}
        images={previewImages}
        title={previewTitle}
        onClose={() => setIsImagePreviewOpen(false)}
      />
    </Dialog>
  );
};

export default observer(StockCheckDetailsPopup);
